import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, Package, FileText, IndianRupee, MapPin, UserCheck, Printer } from "lucide-react";
import AuditTrail from "../components/AuditTrail";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  RECEIVED: "bg-blue-50 text-blue-700 border-blue-200",
  INSPECTED: "bg-amber-50 text-amber-700 border-amber-200",
  ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
  RETURNED: "bg-slate-100 text-slate-600 border-slate-200",
};

const Section = ({ icon: Icon, title, children }) => (
  <Card>
    <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-5">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 rounded-md bg-slate-900 text-white"><Icon className="w-3.5 h-3.5" /></div>
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

const InfoRow = ({ label, value }) => value ? (
  <div className="py-1.5">
    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</p>
    <p className="text-sm text-slate-900 font-medium mt-0.5">{value}</p>
  </div>
) : null;

function GRNView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/grn/${id}`, getAuthHeader());
        setEntry(r.data);
      } catch { toast.error("Failed to load GRN entry"); navigate("/grn"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!entry) return null;

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="grn-view-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/grn")}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900 font-mono">{entry.grn_number}</h1>
              <Badge variant="outline" className={entry.entry_type === "ASSET" ? "border-blue-200 text-blue-700 bg-blue-50" : "border-violet-200 text-violet-700 bg-violet-50"}>
                {entry.entry_type}
              </Badge>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[entry.status] || ""}`}>{entry.status}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Created {new Date(entry.created_at).toLocaleString('en-IN')} by {entry.created_by_name}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" />Print</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={Package} title="Funding & Classification">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="Source of Fund" value={entry.source_of_fund === "INSTITUTE" ? "Institute Funds" : "Project Funds"} />
            <InfoRow label="Project / Fund No." value={entry.project_number} />
            <InfoRow label="Account Category" value={entry.category_as_per_accounts} />
            <InfoRow label="Financial Year" value={entry.financial_year} />
            <InfoRow label="Fund Category" value={entry.fund_category} />
          </div>
        </Section>

        <Section icon={FileText} title="Procurement Details">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="Indent Reference" value={entry.indent_reference_no} />
            <InfoRow label="Indentor" value={entry.indentor_name} />
            <InfoRow label="Department" value={entry.indentor_department} />
            <InfoRow label="Purchase Order" value={entry.purchase_order_no} />
            <InfoRow label="PO Date" value={entry.purchase_order_date} />
            <InfoRow label="Vendor" value={entry.vendor_name} />
            <InfoRow label="Date of Receipt" value={entry.date_of_receipt ? new Date(entry.date_of_receipt).toLocaleDateString('en-IN') : null} />
            <InfoRow label="Invoice No." value={entry.invoice_no} />
            <InfoRow label="Invoice Date" value={entry.invoice_date} />
          </div>
        </Section>
      </div>

      <Section icon={IndianRupee} title="Items Received">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-12 font-semibold">#</TableHead>
              <TableHead className="font-semibold">Description</TableHead>
              <TableHead className="font-semibold">HSN</TableHead>
              <TableHead className="font-semibold">Category / Subcategory</TableHead>
              <TableHead className="font-semibold">Make / Model</TableHead>
              <TableHead className="font-semibold">Serial</TableHead>
              <TableHead className="font-semibold text-right">Qty</TableHead>
              <TableHead className="font-semibold text-right">Basic</TableHead>
              <TableHead className="font-semibold text-right">GST</TableHead>
              <TableHead className="font-semibold text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(entry.items || []).map((item, idx) => (
              <TableRow key={idx}>
                <TableCell className="font-mono text-xs text-slate-500">{item.serial_no}</TableCell>
                <TableCell className="font-medium text-slate-900">{item.description}</TableCell>
                <TableCell className="text-slate-500 text-sm">{item.hsn_code || "—"}</TableCell>
                <TableCell className="text-slate-600 text-xs">
                  {item.asset_category ? (
                    <div className="leading-tight">
                      <div className="font-medium text-slate-700">{item.asset_category}</div>
                      {item.subcategory && <div className="text-slate-500">{item.subcategory}</div>}
                    </div>
                  ) : (item.classification || "—")}
                </TableCell>
                <TableCell className="text-slate-600 text-sm">{[item.make, item.model].filter(Boolean).join(" / ") || "—"}</TableCell>
                <TableCell className="text-slate-500 font-mono text-xs">{item.item_serial_number || "—"}</TableCell>
                <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                <TableCell className="text-right text-sm">₹{(item.basic_price || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell className="text-right text-sm">₹{(item.gst_charges || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell className="text-right font-semibold">₹{(item.total_amount || 0).toLocaleString('en-IN')}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-slate-900 text-white">
              <TableCell colSpan={9} className="text-right font-semibold text-sm">Grand Total</TableCell>
              <TableCell className="text-right font-bold text-lg">₹{(entry.total_value || 0).toLocaleString('en-IN')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>

      {entry.entry_type === "ASSET" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Section icon={MapPin} title="Asset Register & Location">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow label="Accounting Voucher" value={entry.accounting_voucher_no} />
              <InfoRow label="Ledger/Book No." value={entry.ledger_book_no} />
              <InfoRow label="Asset Entry Date" value={entry.asset_entry_date} />
              <InfoRow label="PIR In Name Of" value={entry.pir_in_name_of} />
              <InfoRow label="Asset Holder" value={entry.asset_holder_name} />
              <InfoRow label="Department" value={entry.asset_department} />
              <InfoRow label="Location" value={entry.asset_location} />
              <InfoRow label="Barcode" value={entry.barcode} />
            </div>
            {entry.stores_remarks && <div className="mt-3 p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Stores Remarks</p><p className="text-sm mt-1">{entry.stores_remarks}</p></div>}
          </Section>

          <Section icon={UserCheck} title="Verification & Project">
            <div className="space-y-1">
              <InfoRow label="Asset Verifier" value={entry.asset_verifier_name} />
              <InfoRow label="Physical Verifier Remarks" value={entry.physical_verifier_remarks} />
              <InfoRow label="Stores Section Remarks" value={entry.stores_section_remarks} />
              <InfoRow label="Project Name" value={entry.project_name} />
              <InfoRow label="Principal Investigator" value={entry.principal_investigator} />
              <InfoRow label="Remarks" value={entry.remarks} />
            </div>
          </Section>

          <AuditTrail entries={entry.audit_trail || []} />
        </div>
      )}
    </div>
  );
}

export default GRNView;
